import tinycolor from 'tinycolor2'

import { GRADIENT_POINTS_COUNT } from '../constants/common'

import { getHSVColorSaturation, getHSVColorValue, isWhiteColor } from './color'

function getMidpoint({ x: x1, y: y1 }, { x: x2, y: y2 }) {
  return { x: (x1 + x2) / 2, y: (y1 + y2) / 2 }
}

function getDistance({ x: x1, y: y1 }, { x: x2, y: y2 }) {
  return Math.sqrt(Math.pow(y2 - y1, 2) + Math.pow(x2 - x1, 2))
}

function interpolate(a, b, frac) {
  return {
    x: a.x + (b.x - a.x) * frac,
    y: a.y + (b.y - a.y) * frac,
  }
}

//generate N point in a line
function generateOnLineEveryDistance(
  firstPoint,
  nextPoint,
  pointsCount,
  distance
) {
  const res = []

  for (let i = 0; i < pointsCount; i++) {
    // add a point interpolating on a line after (i + 1) * distance from the beginning point (i+1 to avoid the starting point 0,0)
    res.push(interpolate(firstPoint, nextPoint, (i + 1) * distance))
  }

  return res
}

function generateMidpoints(points, requiredPoint) {
  const midpoints = []
  let closest = null

  for (let i = 0; i < points.length - 1; i++) {
    const coords = getMidpoint(points[i], points[i + 1])
    const distanceToRequiredPoint = getDistance(coords, requiredPoint)

    if (!closest || distanceToRequiredPoint < closest.distanceToRequiredPoint) {
      closest = { ...coords, distanceToRequiredPoint, index: i }
    }

    midpoints.push(coords)
  }

  return [midpoints, closest]
}

function generate(points, pointsCount) {
  // calculate total distance to find out how distant we have to place the points
  let totalDistance = 0

  for (let i = 1; i < points.length; i++) {
    totalDistance += getDistance(points[i - 1], points[i])
  }
  // distance to place the points
  const pointDistance = totalDistance / (pointsCount - 1)
  // the first point is always included
  let res = [points[0]]
  // now, we will consider a segment as point[i - 1] & point[i], and we will consider only the piece where we can fit point:
  // eg. in a segment long 10 ([x:0, y:0], [x:0, y:10]), and a pointDistance of 4, we will  consider only [x:0, y:0], [x:0, y:8]
  // and the remainder is 2... this remainder will be used in the next segment, "appending it at the beginning"
  // let's say the second segment is [x:0, y:10], [x:0, y:20], with the remainder it will be [x:0, y:8], [x:0, y:20]
  let remainder = 0

  for (let i = 1; i < points.length; i++) {
    // add the remainder if exists at the beginning of the current segment (point[i-1], point[i])
    // source https://stackoverflow.com/questions/7740507/extend-a-line-segment-a-specific-distance
    if (remainder > 0) {
      const a = points[i]
      const b = points[i - 1]
      const lengthAB = getDistance(a, b)

      points[i - 1].x = b.x + ((b.x - a.x) / lengthAB) * remainder
      points[i - 1].y = b.y + ((b.y - a.y) / lengthAB) * remainder
    }

    // points we need to generate
    const pointsCount = Math.floor(
      getDistance(points[i - 1], points[i]) / pointDistance
    )

    // remainder to add to the next iteration
    remainder =
      getDistance(points[i - 1], points[i]) - pointsCount * pointDistance
    // add to the result the points
    res = [
      ...res, // previous result
      ...generateOnLineEveryDistance(
        // new points
        points[i - 1], // first point to consider
        points[i], // second point to consider
        pointsCount, // number of points to generate
        pointDistance / getDistance(points[i - 1], points[i]) // the ratio we want to use to generate them
      ),
    ]
  }

  // small fix in case of .333333 decimals that will "miss" the last point because it's .9999 and not 1.00000
  if (res.length < pointsCount) {
    res = [...res, points[points.length - 1]]
  }

  return res
}

export function generateColorsFromGradient(
  position,
  headerGradient,
  buttonGradient
) {
  let headerHoverIndex
  let headerTextIndex
  let sideMenubgIndex
  let sideMenuHoverIndex
  let sideMenubgColor

  if ((position > 0 && position < 5) || position > 8) {
    headerHoverIndex = position - 1
  } else {
    headerHoverIndex = position + 1
  }

  if (position < 5) {
    headerTextIndex = position + 5
  } else if (position > 4 && position < 8) {
    headerTextIndex = position - 5
  } else {
    headerTextIndex = 3
  }

  if (position === 0) {
    sideMenubgColor = '#ffffff'
  } else if (position === 1) {
    sideMenubgIndex = 0
  } else {
    sideMenubgIndex = 1
  }

  if (sideMenubgIndex === 0) {
    sideMenuHoverIndex = 1
  } else if (sideMenubgIndex === 1) {
    sideMenuHoverIndex = 0
  }

  const headerColors = [
    {
      color: headerGradient[position],
      index: 1,
      target: ['header_bg'],
    },
    {
      color: headerGradient[headerHoverIndex],
      index: 2,
      target: ['header_hover', 'switcher_lines'],
    },
    {
      color: headerGradient[headerTextIndex],
      index: 3,
      target: ['header_text', 'header_icons', 'switcher_icon'],
    },
    {
      color: sideMenubgColor
        ? sideMenubgColor
        : headerGradient[sideMenubgIndex],
      index: 0,
      target: ['sidemenu_bg'],
    },
    ...(!sideMenubgColor
      ? [
          {
            color: headerGradient[sideMenuHoverIndex],
            index: 4,
            target: ['sidemenu_hover', 'sidemenu_lines'],
          },
        ]
      : []),
  ]

  const isWhiteHeader = isWhiteColor(headerGradient[position])

  let targetList = isWhiteHeader
    ? ['submenu_hover', 'sidemenu_hover', 'header_hover', 'sidemenu_lines']
    : ['submenu_hover']

  if (sideMenubgColor && !isWhiteHeader) {
    targetList = ['submenu_hover', 'sidemenu_hover', 'sidemenu_lines']
  }

  const buttonColors = [
    { color: buttonGradient[0], index: 0, target: targetList },
    {
      color: buttonGradient[5],
      index: 4,
      target: [
        'button_bg',
        'active_element',
        'link',
        'counter',
        'submenu_button_icon',
      ],
    },
    {
      color: buttonGradient[6],
      index: 5,
      target: ['pressed_button_bg', 'pressed_element'],
    },
    {
      color: buttonGradient[1],
      index: 1,
      target: ['deactive_button_bg'],
    },
    {
      color: buttonGradient[3],
      index: 3,
      target: ['deactive_button_text'],
    },
    {
      color: buttonGradient[2],
      index: 2,
      target: ['submenu_button_bg'],
    },
  ]

  return { button: buttonColors, header: headerColors }
}

export function getColorSet(data) {
  const color = tinycolor(data)

  const hsl = color.toHsl()
  const hsv = color.toHsv()
  const rgb = color.toRgb()
  const hex = color.toHexString()

  return {
    hex,
    hsl,
    hsv,
    rgb,
  }
}

export function getGradient({
  gradientLength = GRADIENT_POINTS_COUNT,
  height,
  hue,
  requiredPoint,
  width,
}) {
  const [generated, closest] = generateMidpoints(
    generate(
      [{ x: 0, y: 0 }, { ...requiredPoint }, { x: width, y: height }],
      gradientLength
    ),
    requiredPoint
  )

  const gradient = generated.map(item => {
    const x = closest.x === item.x ? requiredPoint.x : item.x
    const y = closest.y === item.y ? requiredPoint.y : item.y

    return tinycolor({
      a: 1,
      h: hue,
      s: getHSVColorSaturation(x, width),
      source: 'hsv',
      v: getHSVColorValue(y, height),
    }).toHexString()
  })

  return {
    gradient,
    requiredPointIndex: closest.index,
  }
}

export function createGradientFromColor({ color, height, width }) {
  const {
    hsv: { h: hue, s: saturation, v: value },
  } = color

  const xOffset = saturation * width
  const yOffset = (1 - value) * height

  const { gradient, requiredPointIndex } = getGradient({
    height,
    hue,
    requiredPoint: { x: xOffset, y: yOffset },
    width,
  })

  return { gradient, requiredPointIndex }
}
